import { useEffect, useMemo, useRef, useState } from 'react';
import { LayoutDashboard } from 'lucide-react';
import { updateUiPanelLayout, updateUiPanelVisibility } from '../../api';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import { useToast } from '../../hooks/use-toast';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import {
  canHidePanel,
  itemMinWidth,
  layoutItemTitle,
  layoutTabTitle,
  staticItemKind
} from '../../config/sectionLayouts';
import { Button } from '../ui/button';
import { cellStyle, GRID_CELL_CLASS, GRID_CLASS } from './gridCell';
import {
  changeDraftColumns,
  draftSnapshot,
  ensureItems,
  mergeWithDefaults,
  moveItem,
  resizeItem,
  stepItem,
  updateVisible,
  visibleLayout
} from './gridLayout';
import LayoutEditToolbar from './LayoutEditToolbar';
import LayoutItemFrame, { canResizeHeight } from './LayoutItemFrame';
import { useLayoutDrag } from './useLayoutDrag';

/** Раскладка действует от `xl`: ниже элементы идут одной колонкой, и править там нечего. */
export const LAYOUT_MEDIA_QUERY = '(min-width: 1280px)';

const RESIZE_STEPS = {
  wider: { w: 1, h: 0 },
  narrower: { w: -1, h: 0 },
  taller: { w: 0, h: 1 },
  shorter: { w: 0, h: -1 }
};

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

const Placeholder = ({ hint }) => (
  <div className="flex min-h-24 items-center justify-center rounded-xl border border-dashed bg-muted/40 p-4 pt-10 text-center text-sm text-muted-foreground">
    {hint ?? 'Сейчас этого элемента на вкладке нет'}
  </div>
);

/**
 * Вкладка на сетке. Раздел отдаёт сюда то, что нарисовал (`entries`: `{ id, element, title?,
 * kind?, hide? }`), и раскладку по умолчанию (`defaults`); сетка решает только, где что стоит.
 * Загрузки, фильтры и гарды видимости остаются у раздела: скрытая панель не должна опрашивать
 * сервер, и решать это на уровне сетки было бы поздно.
 *
 * `hiddenEntries` — скрытые настройкой карточки нод (`{ id, title, show }`); скрытые панели
 * реестра сетка находит сама. `placeholders` — известные, но сейчас не нарисованные элементы
 * (`{ id, title, hint }`): в режиме правки они видны заглушками, чтобы и их можно было поставить.
 */
const SectionGrid = ({ tab, entries, defaults, hiddenEntries = [], placeholders = [] }) => {
  const panels = usePanelSettings();
  const { toast } = useToast();
  const isWide = useMediaQuery(LAYOUT_MEDIA_QUERY);
  const gridRef = useRef(null);
  const [draft, setDraft] = useState(() => panels.layoutDrafts.get(tab));
  const [isBusy, setIsBusy] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  const saved = panels.layoutFor(tab);
  const merged = useMemo(() => mergeWithDefaults(defaults, saved), [defaults, saved]);
  const editing = draft !== null && panels.canEditLayout && isWide;
  const columns = editing ? draft.columns : merged.columns;

  const entryById = new Map(entries.map((entry) => [entry.id, entry]));
  const placeholderById = new Map(placeholders.map((entry) => [entry.id, entry]));
  const kindOf = (id) => entryById.get(id)?.kind ?? staticItemKind(tab, id);
  const minWidthOf = (id) => itemMinWidth(tab, id, kindOf(id), columns);
  const titleOf = (id) =>
    entryById.get(id)?.title ?? placeholderById.get(id)?.title ?? layoutItemTitle(id, panels);

  const visibleIds = new Set(entries.map((entry) => entry.id));
  if (editing) {
    placeholders.forEach((entry) => visibleIds.add(entry.id));
  }

  const layout = visibleLayout(
    ensureItems(editing ? draft.items : merged.items, visibleIds, columns),
    visibleIds,
    columns,
    minWidthOf
  );

  // Несохранённая правка не должна теряться молча при закрытии вкладки браузера.
  useEffect(() => {
    if (!editing) {
      return undefined;
    }

    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editing]);

  const commitDraft = (next) => {
    panels.layoutDrafts.set(tab, next);
    setDraft(next);
    return next;
  };

  const describeItem = (nextDraft, id) => {
    const item = visibleLayout(nextDraft.items, visibleIds, nextDraft.columns, minWidthOf)
      .find((entry) => entry.id === id);
    return item
      ? `«${titleOf(id)}»: столбец ${item.x + 1}, строка ${item.y + 1}, ширина ${item.w} из ${nextDraft.columns}`
      : '';
  };

  const applyDraft = (operation) => commitDraft(updateVisible(
    { ...draft, items: ensureItems(draft.items, visibleIds, draft.columns) },
    visibleIds,
    minWidthOf,
    operation
  ));

  const handleMove = (id, target) => applyDraft((items, cols) => moveItem(items, id, target, cols));

  const handleResize = (id, size) => applyDraft((items, cols) => {
    const current = items.find((item) => item.id === id);
    const h = canResizeHeight(kindOf(id)) ? size.h : current?.h ?? 1;
    return resizeItem(items, id, { w: size.w, h }, cols, minWidthOf(id));
  });

  const handleMoveStep = (id, direction) => {
    const next = applyDraft((items, cols) => stepItem(items, id, direction, cols));
    setAnnouncement(describeItem(next, id));
  };

  const handleResizeStep = (id, step) => {
    const current = layout.find((item) => item.id === id);
    const change = RESIZE_STEPS[step];
    if (!current || !change) {
      return;
    }

    handleResize(id, { w: current.w + change.w, h: current.h + change.h });
    setAnnouncement(`«${titleOf(id)}»: ширина ${current.w + change.w} из ${columns}`);
  };

  const dragHandlers = useLayoutDrag({ gridRef, cols: columns, onMove: handleMove, onResize: handleResize });

  const runBusy = async (action, failureTitle) => {
    setIsBusy(true);
    try {
      await action();
    } catch (error) {
      toast({ title: failureTitle, description: describeError(error), variant: 'destructive' });
    } finally {
      setIsBusy(false);
    }
  };

  const setPanelVisible = (id, visible) => runBusy(
    async () => panels.refresh(await updateUiPanelVisibility(id, visible)),
    'Не удалось изменить состав вкладки'
  );

  const hideOf = (id) => {
    const entry = entryById.get(id);
    if (entry?.hide) {
      return () => runBusy(entry.hide, 'Не удалось скрыть элемент');
    }

    return canHidePanel(id) ? () => setPanelVisible(id, false) : undefined;
  };

  const hidden = editing
    ? [
      ...defaults
        .filter((item) => canHidePanel(item.id) && !panels.isVisible(item.id))
        .map((item) => ({ id: item.id, title: panels.title(item.id), show: () => setPanelVisible(item.id, true) })),
      ...hiddenEntries.map((entry) => ({
        ...entry,
        show: () => runBusy(entry.show, 'Не удалось показать элемент')
      }))
    ]
    : [];

  const startEdit = () => commitDraft({
    columns: merged.columns,
    items: ensureItems(merged.items, visibleIds, merged.columns),
    orphans: merged.orphans,
    baseRevision: saved?.revision ?? 0
  });

  const save = () => runBusy(async () => {
    const response = await updateUiPanelLayout(
      tab,
      draft.columns,
      draftSnapshot(draft, visibleIds, minWidthOf),
      draft.baseRevision
    );
    panels.refresh(response);
    commitDraft(null);
    toast({ title: 'Раскладка сохранена', description: 'Клиенты увидят её при следующем открытии вкладки.' });
  }, 'Не удалось сохранить раскладку');

  const reset = () => runBusy(async () => {
    panels.refresh(await updateUiPanelLayout(tab, draft.columns, null, draft.baseRevision));
    commitDraft(null);
    toast({ title: 'Вкладке возвращена раскладка по умолчанию' });
  }, 'Не удалось сбросить раскладку');

  return (
    <div className="flex flex-col gap-3" data-section-layout={tab}>
      {panels.canEditLayout && isWide && !editing && (
        <div className="-mb-1 flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 text-muted-foreground"
            data-ev="layout.edit.open"
            onClick={startEdit}
          >
            <LayoutDashboard />
            Настроить раскладку
          </Button>
        </div>
      )}

      {editing && (
        <LayoutEditToolbar
          tabTitle={panels.tabLabel(tab, layoutTabTitle(tab))}
          columns={columns}
          onColumnsChange={(next) => commitDraft(changeDraftColumns(
            { ...draft, items: ensureItems(draft.items, visibleIds, draft.columns) },
            visibleIds,
            minWidthOf,
            next
          ))}
          onSave={save}
          onCancel={() => commitDraft(null)}
          onReset={reset}
          canReset={saved !== null}
          isBusy={isBusy}
          hidden={hidden}
        />
      )}

      <div ref={gridRef} data-section-grid={tab} className={GRID_CLASS} style={{ '--grid-cols': columns }}>
        {layout.map((item) => {
          const content = entryById.get(item.id)?.element
            ?? <Placeholder hint={placeholderById.get(item.id)?.hint} />;

          if (!editing) {
            return (
              <div key={item.id} data-layout-item={item.id} className={GRID_CELL_CLASS} style={cellStyle(item)}>
                {content}
              </div>
            );
          }

          return (
            <LayoutItemFrame
              key={item.id}
              item={item}
              title={titleOf(item.id)}
              kind={kindOf(item.id)}
              cols={columns}
              minWidth={minWidthOf(item.id)}
              onMoveStep={(direction) => handleMoveStep(item.id, direction)}
              onResizeStep={(step) => handleResizeStep(item.id, step)}
              onHide={hideOf(item.id)}
              dragHandlers={dragHandlers(item, 'move')}
              resizeHandlers={dragHandlers(item, 'resize')}
            >
              {content}
            </LayoutItemFrame>
          );
        })}
      </div>

      {editing && <p className="sr-only" aria-live="polite">{announcement}</p>}
    </div>
  );
};

export default SectionGrid;
